import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Download, CheckCircle,
  Package, Lock, Globe, Link as LinkIcon
} from 'lucide-react';

export default function CustomSkills() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    onConfirm: null,
    skillName: '',
    action: '' // 'uninstall' or 'delete'
  });

  useEffect(() => {
    fetchCustomSkills();
  }, []);

  const fetchCustomSkills = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/skills/custom');
      setSkills(response.data);
    } catch (error) {
      console.error('Failed to fetch custom skills:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (skillPackageId, skillName, isInstalled) => {
    // If skill is installed, only uninstall it on first click
    if (isInstalled) {
      setConfirmDialog({
        open: true,
        title: 'Uninstall Skill First',
        description: `"${skillName}" is currently installed. You must uninstall it before deletion. Would you like to uninstall it now?`,
        skillName: skillName,
        action: 'uninstall',
        onConfirm: async () => {
          try {
            await apiClient.delete(`/skills/uninstall/${skillPackageId}`);
            toast.success('Skill uninstalled. You can now delete it if needed.');
            setConfirmDialog({...confirmDialog, open: false});
            await fetchCustomSkills();
          } catch (error) {
            console.error('Failed to uninstall skill:', error);
            toast.error(error.response?.data?.detail || 'Failed to uninstall skill');
          }
        }
      });
    } else {
      // If not installed, proceed with deletion
      setConfirmDialog({
        open: true,
        title: 'Delete Custom Skill',
        description: `Are you sure you want to permanently delete "${skillName}"? This action cannot be undone.`,
        skillName: skillName,
        action: 'delete',
        onConfirm: async () => {
          try {
            await apiClient.delete(`/skills/custom/${skillPackageId}`);
            toast.success('Custom skill deleted successfully');
            setConfirmDialog({...confirmDialog, open: false});
            await fetchCustomSkills();
          } catch (error) {
            console.error('Failed to delete skill:', error);
            toast.error(error.response?.data?.detail || 'Failed to delete skill');
          }
        }
      });
    }
  };

  const handleInstall = async (skill) => {
    try {
      await apiClient.post('/skills/install', {
        skillPackageId: skill.skillPackageId,
        twinId: 'default-twin',
        enabled: true,
        approvalMode: 'auto',
        allowedDataSources: {
          textTraining: true,
          vault: true
        }
      });

      toast.success('Skill installed successfully');
      await fetchCustomSkills();
    } catch (error) {
      console.error('Failed to install skill:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          'Failed to install skill';
      toast.error(errorMessage);
    }
  };

  const handleUninstall = async (skillPackageId, skillName) => {
    setConfirmDialog({
      open: true,
      title: 'Uninstall Skill',
      description: `Are you sure you want to uninstall "${skillName}"? You can reinstall it anytime.`,
      skillName: skillName,
      action: 'uninstall',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/skills/uninstall/${skillPackageId}`);
          toast.success('Skill uninstalled successfully');
          setConfirmDialog({...confirmDialog, open: false});
          await fetchCustomSkills();
        } catch (error) {
          console.error('Failed to uninstall skill:', error);
          toast.error(error.response?.data?.detail || 'Failed to uninstall skill');
        }
      }
    });
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private': return <Lock className="w-4 h-4" />;
      case 'unlisted': return <LinkIcon className="w-4 h-4" />;
      case 'public': return <Globe className="w-4 h-4" />;
      default: return <Lock className="w-4 h-4" />;
    }
  };

  const getVisibilityLabel = (visibility) => {
    switch (visibility) {
      case 'private': return 'Private';
      case 'unlisted': return 'Unlisted';
      case 'public': return 'Public';
      default: return visibility;
    }
  };

  const getVisibilityColor = (visibility) => {
    switch (visibility) {
      case 'private': return 'bg-gray-100 text-gray-700';
      case 'unlisted': return 'bg-yellow-100 text-yellow-700';
      case 'public': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'high': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading custom skills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="custom-skills-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-gray-900">Custom Skills</h1>
        </div>
        <p className="text-gray-600">
          Create and manage your own private skills for your Twin
        </p>
      </div>

      {/* Create Button */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">
          {skills.length} custom skill{skills.length !== 1 ? 's' : ''}
        </p>
        <Button 
          onClick={() => navigate('/personal/twin/custom-skills/new')}
          data-testid="create-custom-skill-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Custom Skill
        </Button>
      </div>

      {/* Skills List */}
      {skills.length === 0 ? (
        <Card className="p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No custom skills yet</h3>
          <p className="text-gray-600 mb-6">
            Create your own skills tailored to your specific needs
          </p>
          <Button onClick={() => navigate('/personal/twin/custom-skills/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create Your First Skill
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {skills.map((skill) => (
            <Card 
              key={skill.id} 
              className="p-6 hover:shadow-lg transition-shadow"
              data-testid={`custom-skill-card-${skill.skillPackageId}`}
            >
              <div className="space-y-4">
                {/* Header */}
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 flex-1 line-clamp-2">
                      {skill.name}
                    </h3>
                    {skill.isInstalled && (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 ml-2" />
                    )}
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">
                      {skill.category}
                    </Badge>
                    <Badge className={getVisibilityColor(skill.visibility) + " text-xs"}>
                      <span className="mr-1">{getVisibilityIcon(skill.visibility)}</span>
                      {getVisibilityLabel(skill.visibility)}
                    </Badge>
                    <Badge className={getRiskColor(skill.riskLevel) + " text-xs"}>
                      {skill.riskLevel} risk
                    </Badge>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 line-clamp-3 min-h-[60px]">
                  {skill.description}
                </p>

                {/* Tags */}
                {skill.tags && skill.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {skill.tags.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        #{tag}
                      </Badge>
                    ))}
                    {skill.tags.length > 3 && (
                      <Badge variant="secondary" className="text-xs">
                        +{skill.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  {!skill.isInstalled ? (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleInstall(skill)}
                      className="flex-1"
                      data-testid={`install-skill-${skill.skillPackageId}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Install
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUninstall(skill.skillPackageId, skill.name)}
                      className="flex-1"
                      data-testid={`uninstall-skill-${skill.skillPackageId}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Uninstall
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/personal/twin/custom-skills/edit/${skill.skillPackageId}`)}
                    data-testid={`edit-skill-${skill.skillPackageId}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(skill.skillPackageId, skill.name, skill.isInstalled)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-skill-${skill.skillPackageId}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Footer */}
                <div className="text-xs text-gray-500 pt-2 border-t">
                  Created: {new Date(skill.createdAt).toLocaleDateString()}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({...confirmDialog, open})}>
        <AlertDialogContent className="bg-white/95 backdrop-blur-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600">
              {confirmDialog.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmDialog({...confirmDialog, open: false})}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={confirmDialog.action === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-orange-600 hover:bg-orange-700'}
            >
              {confirmDialog.action === 'delete' ? 'Delete' : 'Uninstall'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
